/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { camelize } from '@ember/string';
import { didCancel, restartableTask, dropTask } from 'ember-concurrency';
import { getObservable, queryManager } from 'ember-apollo-client';
import { registerDestructor } from '@ember/destroyable';
import { variation } from 'ember-launch-darkly';

const DEFAULT_RESULTS_PER_PAGE = 10;

export default class ListLoadMore extends Component {
  @service('browser/window') window;
  @queryManager apollo;

  constructor() {
    super(...arguments);
    this.queryTask.perform(this.variables);

    registerDestructor(this, () => {
      this.observeCacheSub?.unsubscribe();
    });
  }

  @tracked hasMoreResults = false;
  @tracked isOffline = false;
  @tracked perPage = parseInt(this.args.perPage) || DEFAULT_RESULTS_PER_PAGE;
  @tracked results = [];

  get canLoadMoreResults() {
    return this.hasMoreResults && !this.isOffline;
  }

  get cursor() {
    return this.window.btoa(this.offset);
  }

  get isEmpty() {
    return !this.isLoading && this.results?.length === 0;
  }

  get isLoading() {
    return this.queryTask.isRunning;
  }

  get isLoadingFirst() {
    return this.isLoading && !this.isLoadingMore;
  }

  get isLoadingMore() {
    return this.results?.length > 0 && this.isLoading;
  }

  get offset() {
    return this.page * this.perPage - this.perPage;
  }

  get page() {
    const resultsCount = this.results?.length || 0;
    return Math.floor(resultsCount / this.perPage) + 1;
  }

  get variables() {
    return {
      ...(this.args.queryVariables || {}),
      first: this.perPage,
      after: this.cursor,
    };
  }

  @restartableTask
  *queryTask(variables = {}) {
    try {
      let queryResult;
      if (!this.observable) {
        queryResult = yield this.args.queryTask.linked().perform(variables);
        this.observable = getObservable(queryResult);
        this.observeCacheSub = this.observable.subscribe(() =>
          this.setFeedFromCache()
        );
      } else {
        yield this.observable.fetchMore({ variables });
        this.args.onAfterFetchMore?.();
      }
    } catch (e) {
      if (e.networkError && !e.networkError.statusCode) {
        if (variation('release-offline-handled-in-place')) {
          this.isOffline = true;
          return;
        }
      }
      this.isOffline = false;

      if (!didCancel(e)) {
        throw e;
      }
    }

    this.isOffline = false;
  }

  @action
  setFeedFromCache() {
    const inCache = this.apollo.apollo.client.readQuery({
      query: this.args.query,
      variables: this.variables,
    });
    const queryName = camelize(this.observable.queryName);
    const inCacheBucket =
      inCache?.[queryName] ?? inCache?.[`${queryName}Connection`];

    this.hasMoreResults = inCacheBucket?.pageInfo.hasNextPage ?? true;
    this.results = inCacheBucket?.edges.map((edge) => edge.node) ?? [];
  }

  @action
  loadMore() {
    return this.queryTask.perform(this.variables);
  }

  @dropTask
  *loadAll() {
    const originalPerPage = this.perPage;
    this.perPage = 500;
    while (this.hasMoreResults) {
      yield this.loadMore();
    }
    this.perPage = originalPerPage;
  }

  @dropTask
  *refetch() {
    if (this.observable) {
      yield this.observable.refetch();
      this.setFeedFromCache();
    }
  }

  @action
  reset() {
    this.results = [];
    this.hasMoreResults = false;
    this.observable = null;
    this.observeCacheSub?.unsubscribe();
    return this.queryTask.perform(this.variables);
  }
}
